import { previewReadOnlyPost, previewResponse, previewState } from './fixtures';

/** Runs before any app/store module. Only a studio-owned frame can select this entry. */
export function prepareStudioPreview(): boolean {
  if (typeof window === 'undefined' || window.parent === window || !window.frameElement?.hasAttribute('data-screen-builder-preview')) return false;
  const options = () => ({ scenario: (window.frameElement as any)?.studioPreviewBridge?.scenario ?? 'populated' });
  const memory = new Map<string, string>([
    ['@eleplan/id_token', 'studio-preview'], ['@eleplan/access_token', 'studio-preview'],
    ['@eleplan/expires_at', String(Date.now() + 86400000)],
  ]);
  if (options().scenario !== 'setup') {
    memory.set('@eleplan/benefits_intro_seen', 'true');
    memory.set('@eleplan/inbox_intro_seen', 'true');
    memory.set('@eleplan/snapshots_intro_seen', '1');
    memory.set('@eleplan/health_showcase_shown', 'true');
    for (const feature of ['vault', 'smart_reminders', 'reminders', 'fasten']) memory.set(`@eleplan/${feature}_intro_seen`, 'true');
  }
  const storage = { getItem: (key: string) => memory.get(key) ?? null, setItem: (key: string, value: string) => { memory.set(key, String(value)); }, removeItem: (key: string) => { memory.delete(key); }, clear: () => memory.clear(), key: (index: number) => [...memory.keys()][index] ?? null, get length() { return memory.size; } };
  Object.defineProperty(window, 'localStorage', { value: storage });
  Object.defineProperty(window, 'sessionStorage', { value: storage });
  Object.defineProperty(window, 'indexedDB', { value: undefined });
  // No backend, device service, telemetry, socket, or external widget may run here.
  const policy = document.createElement('meta');
  policy.httpEquiv = 'Content-Security-Policy';
  // The dev server loads lazy screen chunks with fetch + eval; production uses same-origin script tags.
  policy.content = `script-src 'self' 'unsafe-inline'${__DEV__ ? " 'unsafe-eval'" : ''}; connect-src ${__DEV__ ? "'self'" : "'none'"}; frame-src 'none'; form-action 'none'; media-src 'none'; img-src 'self' data: blob:`;
  document.head.appendChild(policy);
  const devServerFetch = window.fetch?.bind(window);
  window.fetch = async (input, init) => {
    const path = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    if (isDevServerBundle(path)) return devServerFetch(input, init);
    const method = (init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const body = method === 'POST' ? init?.body ?? (input instanceof Request ? await input.clone().text() : undefined) : undefined;
    let request: unknown;
    if (typeof body === 'string') { try { request = JSON.parse(body); } catch { /* Invalid sample requests stay denied. */ } }
    const sample = method === 'POST' ? previewReadOnlyPost(path, request, options()) : previewResponse(path, method, options());
    return new Response(JSON.stringify(sample), { status: 200, headers: { 'Content-Type': 'application/json' } });
  };
  const blocked = () => { throw new Error('Unavailable in screen preview'); };
  const OriginalXHR = window.XMLHttpRequest;
  window.XMLHttpRequest = class extends OriginalXHR { send() { blocked(); } };
  window.WebSocket = class { constructor() { blocked(); } } as any;
  window.EventSource = class { constructor() { blocked(); } } as any;
  if (navigator.sendBeacon) navigator.sendBeacon = () => false;
  (window as any).__ELEPLAN_STUDIO_PREVIEW__ = { seedState: (initial: any) => previewState(initial, options()) };
  return true;
}

function isDevServerBundle(path: string): boolean {
  if (!__DEV__) return false;
  const url = new URL(path, window.location.href);
  return url.origin === window.location.origin && url.pathname.endsWith('.bundle');
}
